import type { PendingInteraction } from '../../types/runtime';
import { useLexRuntimeStore } from '../../store/lexRuntimeStore';

interface Props {
  interaction: PendingInteraction;
}

interface DocumentOption {
  action?: string;
  label?: string;
  document_id?: string | null;
  document_version_id?: string | null;
  ordinal?: number | string | null;
  role_fit?: string | null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function optionRecords(packet: Record<string, unknown>): DocumentOption[] {
  const options = Array.isArray(packet.options) ? packet.options : [];
  return options.filter((option): option is DocumentOption => (
    option !== null && typeof option === 'object' && !Array.isArray(option)
  ));
}

function textList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : [];
}

export function DocumentScopeInteractionCard({ interaction }: Props) {
  const isLoading = useLexRuntimeStore((s) => s.isLoading);
  const resume = useLexRuntimeStore((s) => s.resumeWithInteraction);

  const packet = asRecord(interaction.packet);
  const matter = asRecord(packet.matter);
  const requested = asRecord(packet.requested_document);
  const findings = textList(packet.findings);
  const options = optionRecords(packet);
  const matterLabel = typeof matter.label === 'string' ? matter.label : 'the resolved matter';
  const requestedCues = textList(requested.cues);
  const noOperative = findings.includes('no_obvious_operative_pleading_candidate');
  const conflict = findings.includes('loaded_document_rejected_different_matter');

  return (
    <article className="rounded-lg border-2 border-sky-300 bg-sky-50 p-4 text-sm text-sky-950 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded bg-sky-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-950">
          Scope choice
        </span>
        <span className="text-xs text-sky-800">Document evidence needed</span>
      </div>

      <p className="mb-3 leading-relaxed">{interaction.summary}</p>

      <div className="mb-3 space-y-1 text-xs leading-relaxed text-sky-900">
        <p>
          <span className="font-semibold">Matter:</span> {matterLabel}
        </p>
        {requestedCues.length > 0 && (
          <p>
            <span className="font-semibold">Requested document role:</span> {requestedCues.join(', ')}
          </p>
        )}
        {noOperative && (
          <p>
            I should not build causes of action, elements, damages, defenses, or relief without an operative pleading.
          </p>
        )}
        {conflict && (
          <p>
            I also rejected the prior loaded document because it belongs to a different matter.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {options.map((option, index) => {
          const action = option.action || 'select_document';
          const isDocument = action === 'select_document';
          return (
            <button
              key={`${action}-${option.document_id ?? option.ordinal ?? index}`}
              type="button"
              disabled={isLoading}
              onClick={() => resume({
                action,
                document_id: option.document_id ?? undefined,
                document_version_id: option.document_version_id ?? undefined,
                ordinal: option.ordinal ?? undefined,
                basis: 'user_selected_document_scope_interaction',
              })}
              className="rounded border border-sky-300 bg-white px-3 py-2 text-left text-xs font-medium text-sky-950 shadow-sm hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isDocument && option.ordinal ? `${option.ordinal}. ` : ''}
              {option.label || 'Use this option'}
              {option.role_fit === 'not_obvious_operative_pleading' && (
                <span className="ml-2 font-normal text-sky-700">(not an obvious operative pleading)</span>
              )}
            </button>
          );
        })}
      </div>
    </article>
  );
}
